import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ArrowLeft, Plus, FileText, DollarSign, Clock, AlertTriangle,
  CheckCircle, XCircle, Send, Eye, Trash2, MoreVertical, Edit2,
  Calendar, TrendingUp, TrendingDown, Filter, ChevronDown, ChevronUp
} from 'lucide-react';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
  pending_review: { label: 'Pending Review', color: 'bg-blue-100 text-blue-700', icon: Clock },
  pending_client_approval: { label: 'Awaiting Client', color: 'bg-purple-100 text-purple-700', icon: Send },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  voided: { label: 'Voided', color: 'bg-gray-100 text-gray-500', icon: XCircle }
};

const REASON_OPTIONS = [
  { value: 'client_request', label: 'Client Request' },
  { value: 'unforeseen_condition', label: 'Unforeseen Condition' },
  { value: 'design_change', label: 'Design Change' },
  { value: 'error_correction', label: 'Error Correction' },
  { value: 'value_engineering', label: 'Value Engineering' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-gray-500' },
  { value: 'normal', label: 'Normal', color: 'text-blue-500' },
  { value: 'high', label: 'High', color: 'text-amber-500' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-500' }
];

const CATEGORY_OPTIONS = [
  { value: 'labor', label: 'Labor' },
  { value: 'materials', label: 'Materials' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'other', label: 'Other' }
];

export default function ChangeOrdersPage({ projectId = null, embedded = false }) {
  const darkMode = document.documentElement.classList.contains('dark');
  const [changeOrders, setChangeOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({ total_count: 0, approved_count: 0, pending_count: 0, total_cost_impact: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(projectId || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewCO, setShowNewCO] = useState(false);
  const [selectedCO, setSelectedCO] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [sendToClientCO, setSendToClientCO] = useState(null);
  const [sendingToClient, setSendingToClient] = useState(false);
  const [customMessage, setCustomMessage] = useState('');

  const handleSendToClient = async (co) => {
    if (!co) return;
    
    setSendingToClient(true);
    try {
      const response = await axios.post(`${API}/drawing-room/send-document`, {
        project_id: co.project_id,
        document_type: 'change_order',
        document_id: co.id,
        document_name: `Change Order #${co.co_number} - ${co.title}`,
        custom_message: customMessage || null,
        requires_signature: false,
        requires_approval: true
      });
      
      toast.success(response.data.email_sent 
        ? 'Change Order sent to client\'s Drawing Room with email notification!' 
        : 'Change Order added to client\'s Drawing Room');
      setSendToClientCO(null);
      setCustomMessage('');
      fetchData();
    } catch (error) {
      console.error('Error sending to client:', error);
      toast.error(error.response?.data?.detail || 'Failed to send change order to client');
    } finally {
      setSendingToClient(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedProject, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedProject && selectedProject !== 'all') params.append('project_id', selectedProject);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const [coRes, projectsRes] = await Promise.all([
        axios.get(`${API}/change-orders?${params.toString()}`),
        !embedded ? axios.get(`${API}/projects`) : Promise.resolve({ data: [] })
      ]);

      setChangeOrders(coRes.data.change_orders || []);
      setSummary(coRes.data.summary || {});
      if (!embedded) setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load change orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (coId) => {
    try {
      await axios.delete(`${API}/change-orders/${coId}`);
      toast.success('Change order deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
    setShowDeleteConfirm(null);
  };

  const handleSubmit = async (coId) => {
    try {
      await axios.post(`${API}/change-orders/${coId}/submit`, { submitted_by: 'Current User' });
      toast.success('Change order submitted for review');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const formatCurrency = (amount) => {
    const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount || 0));
    if (amount < 0) return `-${formatted}`;
    if (amount > 0) return `+${formatted}`;
    return formatted;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const containerClass = embedded ? '' : 'min-h-screen bg-background';

  return (
    <div className={containerClass}>
      {/* Header - Only show if not embedded */}
      {!embedded && (
        <header className="bg-card border-b border-border sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => window.location.href = '/financials'}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <HelpIcon elementId="change-order" position="inline" />
                  <div>
                    <h1 className="text-2xl font-heading">Change Orders</h1>
                    <p className="text-sm text-muted-foreground">Track scope and cost changes</p>
                  </div>
                </div>
              </div>
              <Button onClick={() => setShowNewCO(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Change Order
              </Button>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6'}>
        {/* Action buttons when embedded */}
        {embedded && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Project Change Orders</h3>
            <Button size="sm" onClick={() => setShowNewCO(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Change Order
            </Button>
          </div>
        )}
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-50 border border-blue-100">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
                <p className="text-xl font-semibold">{summary.total_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-50 border border-amber-100">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Pending</p>
                <p className="text-xl font-semibold">{summary.pending_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-50 border border-green-100">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Approved</p>
                <p className="text-xl font-semibold">{summary.approved_count}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full border ${summary.total_cost_impact >= 0 ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
                {summary.total_cost_impact >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-red-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-green-600" />
                )}
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Cost Impact</p>
                <p className={`text-xl font-semibold ${summary.total_cost_impact >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(summary.total_cost_impact)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          {!embedded && (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="pending_client_approval">Awaiting Client</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>

          {embedded && (
            <Button onClick={() => setShowNewCO(true)} className="ml-auto">
              <Plus className="w-4 h-4 mr-2" />
              New Change Order
            </Button>
          )}
        </div>

        {/* Change Order List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading change orders...</div>
        ) : changeOrders.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-lg">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground mb-4">No change orders yet</p>
            <Button onClick={() => setShowNewCO(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Change Order
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {changeOrders.map(co => {
              const statusConfig = STATUS_CONFIG[co.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div 
                  key={co.id} 
                  className="bg-card border border-border rounded-lg hover:border-[#70170F]/30 transition-colors"
                >
                  {/* CO Header */}
                  <div className="p-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-sm text-[#70170F] font-medium">{co.change_order_number}</span>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                        {co.priority !== 'normal' && (
                          <Badge variant="outline" className={PRIORITY_OPTIONS.find(p => p.value === co.priority)?.color}>
                            {co.priority}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-medium text-foreground mb-1">{co.title}</h3>
                      {!embedded && co.project_name && (
                        <p className="text-sm text-muted-foreground">{co.project_name}</p>
                      )}
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{co.description}</p>
                    </div>
                    
                    {/* Cost Impact */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Cost Impact</p>
                      <p className={`text-xl font-semibold ${co.cost_impact >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(co.cost_impact)}
                      </p>
                      {co.schedule_impact_days !== 0 && (
                        <p className={`text-xs mt-1 ${co.schedule_impact_days > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {co.schedule_impact_days > 0 ? '+' : ''}{co.schedule_impact_days} days
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedCO(co)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {(co.status === 'approved' || co.status === 'pending_client_approval') && (
                          <DropdownMenuItem onClick={() => setSendToClientCO(co)} data-testid={`send-co-${co.id}`}>
                            <Mail className="w-4 h-4 mr-2" />
                            Send to Client
                          </DropdownMenuItem>
                        )}
                        {co.status === 'draft' && (
                          <>
                            <DropdownMenuItem onClick={() => handleSubmit(co.id)}>
                              <Send className="w-4 h-4 mr-2" />
                              Submit for Review
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => setShowDeleteConfirm(co.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  {/* CO Footer */}
                  <div className="px-4 py-2 bg-muted/30 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span>Reason: {REASON_OPTIONS.find(r => r.value === co.reason)?.label || co.reason}</span>
                      {co.phase_name && <span>Phase: {co.phase_name}</span>}
                    </div>
                    <span>Created {formatDate(co.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Change Order Dialog */}
      <NewChangeOrderDialog
        open={showNewCO}
        onOpenChange={setShowNewCO}
        projectId={projectId}
        projects={projects}
        onCreated={() => {
          fetchData();
          setShowNewCO(false);
        }}
      />

      {/* Change Order Detail Dialog */}
      {selectedCO && (
        <ChangeOrderDetailDialog
          changeOrder={selectedCO}
          onClose={() => setSelectedCO(null)}
          onUpdate={fetchData}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Change Order?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the change order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(showDeleteConfirm)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send to Client Dialog */}
      <Dialog open={!!sendToClientCO} onOpenChange={(open) => !open && setSendToClientCO(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#70170F]" />
              Send Change Order to Client
            </DialogTitle>
          </DialogHeader>
          {sendToClientCO && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Change Order</p>
                <p className="font-medium">#{sendToClientCO.co_number} - {sendToClientCO.title}</p>
                <p className="text-sm text-muted-foreground mt-2">Cost Impact</p>
                <p className={`font-medium ${sendToClientCO.cost_impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(sendToClientCO.cost_impact)}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Custom Message (optional)</Label>
                <Textarea
                  placeholder="Add a personal message for the client..."
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  This will be included in the email notification to the client.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <Mail className="w-4 h-4 inline mr-2" />
                  The client will receive an email notification with a link to view and approve this change order in their Drawing Room.
                </p>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setSendToClientCO(null)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleSendToClient(sendToClientCO)}
                  disabled={sendingToClient}
                  className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                  data-testid="confirm-send-co-to-client"
                >
                  {sendingToClient ? 'Sending...' : 'Send to Drawing Room'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// New Change Order Dialog
function NewChangeOrderDialog({ open, onOpenChange, projectId, projects, onCreated }) {
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('client_request');
  const [priority, setPriority] = useState('normal');
  const [costImpact, setCostImpact] = useState('');
  const [scheduleDays, setScheduleDays] = useState('0');
  const [lineItems, setLineItems] = useState([
    { id: '1', description: '', quantity: 1, unit: 'each', unit_cost: 0, category: 'labor' }
  ]);
  const [showLineItems, setShowLineItems] = useState(false);
  const [loading, setLoading] = useState(false);

  const addLineItem = () => {
    setLineItems([...lineItems, {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit: 'each',
      unit_cost: 0,
      category: 'labor'
    }]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(items => items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeLineItem = (id) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter(item => item.id !== id));
    }
  };

  const lineItemsTotal = lineItems.reduce((sum, item) => 
    sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0), 0
  );

  const handleCreate = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setLoading(true);
      
      const data = {
        project_id: selectedProject,
        title,
        description,
        reason,
        priority,
        cost_impact: showLineItems ? lineItemsTotal : parseFloat(costImpact) || 0,
        schedule_impact_days: parseInt(scheduleDays) || 0,
        line_items: showLineItems ? lineItems : []
      };

      await axios.post(`${API}/change-orders`, data);
      toast.success('Change order created');
      
      // Reset form
      setTitle('');
      setDescription('');
      setReason('client_request');
      setPriority('normal');
      setCostImpact('');
      setScheduleDays('0');
      setLineItems([{ id: '1', description: '', quantity: 1, unit: 'each', unit_cost: 0, category: 'labor' }]);
      setShowLineItems(false);
      
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create change order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#70170F]" />
            New Change Order
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Project Selection */}
          {!projectId && (
            <div>
              <Label>Project *</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Title */}
          <div>
            <Label>Title *</Label>
            <Input 
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g., Add recessed lighting to master bedroom"
            />
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <Textarea 
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Detailed description of the change..."
              rows={3}
            />
          </div>

          {/* Reason & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cost Impact Toggle */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base">Cost Impact</Label>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowLineItems(!showLineItems)}
              >
                {showLineItems ? 'Simple Entry' : 'Add Line Items'}
                {showLineItems ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </Button>
            </div>

            {!showLineItems ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Cost Change ($)</Label>
                  <Input 
                    type="number"
                    value={costImpact}
                    onChange={e => setCostImpact(e.target.value)}
                    placeholder="e.g., 2500 or -500"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Positive = increase, Negative = decrease</p>
                </div>
                <div>
                  <Label className="text-sm">Schedule Impact (days)</Label>
                  <Input 
                    type="number"
                    value={scheduleDays}
                    onChange={e => setScheduleDays(e.target.value)}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Positive = delay, Negative = ahead</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {lineItems.map((item, idx) => (
                  <div key={item.id} className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-4">
                      <Input 
                        placeholder="Description"
                        value={item.description}
                        onChange={e => updateLineItem(item.id, 'description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Select value={item.category} onValueChange={v => updateLineItem(item.id, 'category', v)}>
                        <SelectTrigger className="h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORY_OPTIONS.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Input 
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={e => updateLineItem(item.id, 'quantity', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input 
                        type="number"
                        placeholder="Cost"
                        value={item.unit_cost}
                        onChange={e => updateLineItem(item.id, 'unit_cost', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1 text-right pt-2 font-medium">
                      ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0)).toFixed(0)}
                    </div>
                    <div className="col-span-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => removeLineItem(item.id)}
                        disabled={lineItems.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2">
                  <Button variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="w-3 h-3 mr-1" /> Add Item
                  </Button>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total Cost Impact</p>
                    <p className="text-xl font-semibold">${lineItemsTotal.toFixed(2)}</p>
                  </div>
                </div>
                <div className="pt-2">
                  <Label className="text-sm">Schedule Impact (days)</Label>
                  <Input 
                    type="number"
                    value={scheduleDays}
                    onChange={e => setScheduleDays(e.target.value)}
                    placeholder="0"
                    className="w-32"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={loading}>
              Create Change Order
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Change Order Detail Dialog
function ChangeOrderDetailDialog({ changeOrder, onClose, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const co = changeOrder;
  const statusConfig = STATUS_CONFIG[co.status] || STATUS_CONFIG.draft;

  const handleApprove = async (type) => {
    try {
      setLoading(true);
      await axios.post(`${API}/change-orders/${co.id}/approve`, {
        approval_type: type,
        approved_by: 'Current User'
      });
      toast.success(type === 'client' ? 'Change order approved' : 'Change order reviewed');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/change-orders/${co.id}/reject`, {
        reason: 'Rejected by reviewer',
        rejected_by: 'Current User'
      });
      toast.success('Change order rejected');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount || 0));
    if (amount < 0) return `-${formatted}`;
    if (amount > 0) return `+${formatted}`;
    return formatted;
  };

  return (
    <Dialog open={!!changeOrder} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[#70170F]">{co.change_order_number}</span>
              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Title & Description */}
          <div>
            <h3 className="text-lg font-medium mb-2">{co.title}</h3>
            <p className="text-muted-foreground">{co.description || 'No description provided'}</p>
          </div>

          {/* Impact Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Cost Impact</p>
              <p className={`text-2xl font-semibold ${co.cost_impact >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(co.cost_impact)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Schedule Impact</p>
              <p className={`text-2xl font-semibold ${co.schedule_impact_days > 0 ? 'text-amber-600' : co.schedule_impact_days < 0 ? 'text-green-600' : ''}`}>
                {co.schedule_impact_days > 0 ? '+' : ''}{co.schedule_impact_days} days
              </p>
            </div>
          </div>

          {/* Line Items */}
          {co.line_items && co.line_items.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Line Items</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 text-sm">Description</th>
                      <th className="text-left px-3 py-2 text-sm">Category</th>
                      <th className="text-right px-3 py-2 text-sm">Qty</th>
                      <th className="text-right px-3 py-2 text-sm">Cost</th>
                      <th className="text-right px-3 py-2 text-sm">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {co.line_items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 capitalize">{item.category}</td>
                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">${item.unit_cost}</td>
                        <td className="px-3 py-2 text-right font-medium">${item.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Reason</p>
              <p className="font-medium">{REASON_OPTIONS.find(r => r.value === co.reason)?.label}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Priority</p>
              <p className="font-medium capitalize">{co.priority}</p>
            </div>
            {co.project_name && (
              <div>
                <p className="text-muted-foreground">Project</p>
                <p className="font-medium">{co.project_name}</p>
              </div>
            )}
            {co.phase_name && (
              <div>
                <p className="text-muted-foreground">Phase</p>
                <p className="font-medium">{co.phase_name}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {(co.internal_notes || co.client_notes) && (
            <div className="space-y-2">
              {co.internal_notes && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Internal Notes</p>
                  <p className="text-sm">{co.internal_notes}</p>
                </div>
              )}
              {co.client_notes && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 mb-1">Client Notes</p>
                  <p className="text-sm">{co.client_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Close</Button>
            
            {co.status === 'pending_review' && (
              <>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleReject} disabled={loading}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button onClick={() => handleApprove('internal')} disabled={loading}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve & Send to Client
                </Button>
              </>
            )}
            
            {co.status === 'pending_client_approval' && (
              <>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleReject} disabled={loading}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove('client')} disabled={loading}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Client Approved
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
